import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Badge, Button, type ButtonProps, LoadingIndicatorIcon } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import {
    type GitSyncCurrentStatus,
    GitSyncProviderIcon,
    useGitSyncCurrentStatus,
} from '../../../Components';
import { useNavigation } from '../../../navigation';

/**
 * Button to display the current state of the Git Sync to the user.
 */
export function GitSyncStateButton(
    props: { space: api.Space } & Partial<ButtonProps>
): React.ReactElement | null {
    const { space, kind = 'blank', ...rest } = props;

    const { navigate } = useNavigation();
    const gitSyncStatus = useGitSyncCurrentStatus(space);

    return space.gitSync?.installationProvider ? (
        <Button
            {...rest}
            kind={kind}
            className="max-lg:hidden"
            leadingIcon={GitSyncProviderIcon[space.gitSync.installationProvider]}
            {...(gitSyncStatus ? getDescriptionForOperation(gitSyncStatus) : { label: '' })}
            onPress={() => {
                navigate({
                    screen: {
                        sideSheet: { type: 'gitsync', tab: 'status' },
                    },
                });
            }}
        />
    ) : (
        <Button
            {...rest}
            kind={kind}
            label="Set up Git Sync"
            className="max-lg:hidden"
            leadingIcon={GitSyncProviderIcon.github}
            title="Sync this space’s content with GitHub or GitLab"
            onPress={() => {
                navigate({
                    current: {
                        sideSheet: {
                            type: 'gitsync-setup',
                        },
                    },
                });
            }}
        />
    );
}

function getDescriptionForOperation(operation: GitSyncCurrentStatus) {
    switch (operation.state) {
        case 'running':
            return {
                trailingIcon: LoadingIndicatorIcon,
                label: 'Syncing',
                title:
                    operation.direction === 'import'
                        ? 'GitBook is importing changes from your repository'
                        : 'GitBook is exporting your recent changes',
            };
        case 'timed-out':
        case 'failure':
            return {
                trailingIcon: <Badge icon={Icon.X} iconOnly kind="danger" size="xsmall" />,
                label: 'Failure',
                title:
                    operation.direction === 'import'
                        ? 'The latest import failed'
                        : 'The latest export failed',
            };
        case 'success':
            return {
                trailingIcon: <Badge icon={Icon.Check} iconOnly kind="done" size="xsmall" />,
                label: 'Synced',
                title:
                    operation.direction === 'import'
                        ? 'The latest import succeeded'
                        : 'The latest export succeeded',
            };
        default:
            assertNever(operation);
    }
}
